// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect } from 'react';
import Button from 'antd/lib/button';
import Menu from 'antd/lib/menu';
import { Link } from 'react-router-dom';
import Icon, { CheckCircleOutlined, StopOutlined } from '@ant-design/icons';

import {
    ActiveControl, ToolsBlockerState, Workspace,
} from 'reducers';
import { DimensionType } from 'cvat-core-wrapper';
import { UndoIcon, RedoIcon } from 'icons';
import CVATTooltip from 'components/common/cvat-tooltip';
import AnnotationMenuContainer from 'containers/annotation-page/top-bar/annotation-menu';
import WorkspaceBar from 'components/annotation-page/workspace/workspace-bar';
import Breadcrumb from 'components/annotation-page/workspace/breadcrumb';
import SaveStatus from 'components/annotation-page/workspace/save-status';
import MoreMenu from 'components/annotation-page/workspace/more-menu';
import ModeSwitchContainer from 'components/annotation-page/workspace/mode-switch-container';
import { useTaskSummary } from 'components/annotation-page/workspace/use-task-summary';
import { rememberRecentJob } from 'utils/recent-jobs';
import { workspacePath } from 'utils/workspace-route';
import { useGuide } from 'components/annotation-page/workspace/use-guide';
import { t } from 'utils/i18n';

interface Props {
    saving: boolean;
    undoAction?: string;
    redoAction?: string;
    workspace: Workspace;
    undoShortcut: string;
    redoShortcut: string;
    drawShortcut: string;
    switchToolsBlockerShortcut: string;
    activeControl: ActiveControl;
    toolsBlockerState: ToolsBlockerState;
    initialOpenGuide: boolean;
    hasUnsavedChanges: boolean;
    changeWorkspace(workspace: Workspace): void;
    showStatistics(): void;
    showFilters(): void;
    onSaveAnnotation(): void;
    onUndoClick(): void;
    onRedoClick(): void;
    onFinishDraw(): void;
    onSwitchToolsBlockerState(): void;
    onExportBlend?(): void;
    frameDeleted: boolean;
    onRestoreFrame(): void;
    onDeleteFrame?(): void;
    onCopyFrameLink(): void;
    onOpenSettings(): void;
    onOpenShortcuts(): void;
    filtersActive: boolean;
    jobInstance: any;
    taskId: number;
    referenceId: number | null;
}

export default function AnnotationTopBarComponent(props: Props): JSX.Element {
    const {
        saving,
        undoAction,
        redoAction,
        workspace,
        undoShortcut,
        redoShortcut,
        drawShortcut,
        switchToolsBlockerShortcut,
        activeControl,
        toolsBlockerState,
        initialOpenGuide,
        showStatistics,
        showFilters,
        changeWorkspace,
        onSaveAnnotation,
        onUndoClick,
        onRedoClick,
        onFinishDraw,
        onSwitchToolsBlockerState,
        jobInstance,
        hasUnsavedChanges,
        onExportBlend,
        frameDeleted,
        onRestoreFrame,
        onDeleteFrame,
        onCopyFrameLink,
        onOpenSettings,
        onOpenShortcuts,
        filtersActive,
        taskId,
        referenceId,
    } = props;

    const summary = useTaskSummary(taskId);
    const openGuide = useGuide(jobInstance, initialOpenGuide);
    useEffect(() => {
        if (summary.taskName === null) return;
        rememberRecentJob({
            jobId: jobInstance.id,
            taskId,
            referenceId,
            title: summary.taskName,
            subtitle: summary.projectName,
            path: workspacePath(taskId, jobInstance.id, referenceId),
        });
    }, [jobInstance.id, taskId, referenceId, summary.taskName, summary.projectName]);

    const includesDoneButton = [
        ActiveControl.DRAW_POLYGON,
        ActiveControl.DRAW_POLYLINE,
        ActiveControl.DRAW_POINTS,
        ActiveControl.AI_TOOLS,
        ActiveControl.OPENCV_TOOLS,
        ActiveControl.CROP,
    ].includes(activeControl);

    const includesToolsBlockerButton =
        [ActiveControl.OPENCV_TOOLS, ActiveControl.AI_TOOLS].includes(activeControl) && toolsBlockerState.buttonVisible;

    const shouldEnableToolsBlockerOnClick = [ActiveControl.OPENCV_TOOLS].includes(activeControl);

    const drawingActions = includesDoneButton || includesToolsBlockerButton ? (
        <>
            {includesDoneButton ? (
                <CVATTooltip overlay={t('Press “{key}” to finish', { key: drawShortcut })}>
                    <Button type='link' className='cvat-annotation-header-done-button cvat-annotation-header-button' onClick={onFinishDraw}>
                        <CheckCircleOutlined />
                        {t('Done')}
                    </Button>
                </CVATTooltip>
            ) : null}
            {includesToolsBlockerButton ? (
                <CVATTooltip overlay={t('Press “{key}” to postpone running the algorithm', { key: switchToolsBlockerShortcut })}>
                    <Button
                        type='link'
                        className={`cvat-annotation-header-block-tool-button cvat-annotation-header-button ${
                            toolsBlockerState.algorithmsLocked ? 'cvat-button-active' : ''
                        }`}
                        onClick={shouldEnableToolsBlockerOnClick ? onSwitchToolsBlockerState : undefined}
                    >
                        <StopOutlined />
                        {t('Block')}
                    </Button>
                </CVATTooltip>
            ) : null}
        </>
    ) : null;

    const fullscreen = (): void => {
        if (!window.document.fullscreenEnabled) return;
        if (window.document.fullscreenElement) window.document.exitFullscreen();
        else window.document.documentElement.requestFullscreen();
    };

    return (
        <WorkspaceBar
            breadcrumb={<Breadcrumb taskId={taskId} {...summary} />}
            undo={(
                <>
                    <CVATTooltip overlay={t('Undo: {action} {key}', { action: undoAction, key: undoShortcut })}>
                        <Button type='text' aria-label={t('Undo')} disabled={!undoAction || saving} className='cvat-annotation-header-undo-button' icon={<Icon component={UndoIcon} />} onClick={onUndoClick} />
                    </CVATTooltip>
                    <CVATTooltip overlay={t('Redo: {action} {key}', { action: redoAction, key: redoShortcut })}>
                        <Button type='text' aria-label={t('Redo')} disabled={!redoAction || saving} className='cvat-annotation-header-redo-button' icon={<Icon component={RedoIcon} />} onClick={onRedoClick} />
                    </CVATTooltip>
                </>
            )}
            save={<SaveStatus saving={saving} hasUnsavedChanges={hasUnsavedChanges} onSave={onSaveAnnotation} />}
            drawingActions={drawingActions ?? <ModeSwitchContainer taskId={taskId} />}
            more={(
                <MoreMenu
                    menu={referenceId !== null ? (
                        <Menu.Item key='open-reference-editor'>
                            <Link to={`/references/${referenceId}/jobs/${jobInstance.id}`}>
                                {t('Open in reference editor')}
                            </Link>
                        </Menu.Item>
                    ) : <AnnotationMenuContainer />}
                    workspace={workspace}
                    is3D={jobInstance.dimension === DimensionType.DIMENSION_3D}
                    hasGuide={jobInstance.guideId !== null}
                    onWorkspace={changeWorkspace}
                    onFullscreen={fullscreen}
                    onInfo={showStatistics}
                    onFilters={showFilters}
                    onGuide={openGuide}
                    frameDeleted={frameDeleted}
                    onRestoreFrame={onRestoreFrame}
                    onDeleteFrame={onDeleteFrame}
                    onCopyFrameLink={onCopyFrameLink}
                    onSettings={onOpenSettings}
                    onShortcuts={onOpenShortcuts}
                    filtersActive={filtersActive}
                />
            )}
            onExportBlend={onExportBlend}
        />
    );
}
